import { useMemo } from "react";
import { alignCandidates } from "@capture/shared";
import { HighlightedText } from "./HighlightedText";

interface CandidateArbitrationProps {
  candidates: { engine: string; tesseract: string; trocr: string };
  activeEngine: "tesseract" | "trocr";
}

/**
 * Handwriting Phase 3 — the disagreement panel under the active field.
 * Positions where both engines AGREE are near-certain correct; the marked
 * positions are what the keyer actually needs to look at. Keyboard-only by
 * design: Ctrl+E (KeyboardManager) toggles which engine's candidate is in
 * the input — the rows here are indicators, not click targets.
 */
export function CandidateArbitration({ candidates, activeEngine }: CandidateArbitrationProps) {
  const { aSpans, bSpans } = useMemo(
    () => alignCandidates(candidates.tesseract, candidates.trocr),
    [candidates.tesseract, candidates.trocr],
  );

  const rows: Array<{ engine: "tesseract" | "trocr"; label: string; text: string; spans: typeof aSpans }> = [
    { engine: "tesseract", label: "TESSERACT", text: candidates.tesseract, spans: aSpans },
    { engine: "trocr", label: "TROCR", text: candidates.trocr, spans: bSpans },
  ];

  return (
    <div className="candidate-arbitration" role="group" aria-label="OCR engines disagreed — review the marked characters">
      {rows.map((row) => (
        <div
          key={row.engine}
          className={`candidate-row ${activeEngine === row.engine ? "candidate-row--active" : ""}`}
          data-engine={row.engine}
          aria-pressed={activeEngine === row.engine}
        >
          <span className="candidate-engine">{row.label}</span>
          <span className="candidate-text">
            <HighlightedText text={row.text} spans={row.spans} />
          </span>
        </div>
      ))}
      <span className="candidate-hint" aria-hidden="true">
        <kbd>Ctrl</kbd>+<kbd>E</kbd> swap · type to re-key · <kbd>Esc</kbd> restore
      </span>

      <style>{`
        .candidate-arbitration {
          margin-top: var(--space-1);
          padding: var(--space-1) var(--space-2);
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border);
          border-left: 3px solid var(--color-primary);
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
        }
        .candidate-row {
          display: flex;
          align-items: baseline;
          gap: var(--space-2);
          padding: 1px 0;
          color: var(--color-text-muted);
        }
        .candidate-row--active {
          color: var(--color-text-primary);
        }
        .candidate-engine {
          flex-shrink: 0;
          width: 72px;
          font-size: 0.6rem;
          font-weight: 600;
          letter-spacing: 0.05em;
          color: var(--color-text-muted);
        }
        .candidate-row--active .candidate-engine {
          color: var(--color-primary);
        }
        .candidate-row--active .candidate-engine::after {
          content: " \\25CF";
        }
        .candidate-text {
          font-family: "SF Mono", "Fira Code", Consolas, monospace;
        }
        .candidate-text mark {
          background: color-mix(in srgb, var(--color-warning) 30%, transparent);
          color: inherit;
          border-radius: 2px;
          padding: 0 1px;
          font-weight: 700;
        }
        .candidate-hint {
          display: block;
          margin-top: 2px;
          font-size: 0.6rem;
          color: var(--color-text-muted);
        }
        .candidate-hint kbd {
          display: inline-block;
          padding: 0 3px;
          font-family: inherit;
          font-size: 0.55rem;
          background: var(--color-bg-tertiary);
          border: 1px solid var(--color-border);
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
}
